import * as React from 'react';
import {
    createStyles,
    makeStyles,
    Theme,
} from '@bb-ui/react-library/dist/components/styles';
import classNames from 'classnames';
import {
    BlackboardLogo,
    BLACKBOARD_GREEN,
    BLACKBOARD_OFF_WHITE,
} from './BlackboardLogo';

interface IBlackboardIlluminateLogoProps {
    className?: string;
    theme?: 'light' | 'dark';
    orientation?: 'inline' | 'stacked';
    size?: 'default' | 'large';
}

const LOCKUP_BASELINE_OFFSET = `${(
    -((160 - 129.17) / 160) *
    (160 / 879) *
    100
).toFixed(4)}%`;

const styles = (theme: Theme) =>
    createStyles({
        container: {
            display: 'flex',
            alignItems: 'center',
            maxWidth: '100%',
            minWidth: 0,
        },
        inline: {
            flexDirection: 'row',
            alignItems: 'baseline',
            '& > span': {
                marginLeft: theme.spacing(1),
            },
            '& $lockup': {
                marginBottom: LOCKUP_BASELINE_OFFSET,
            },
        },
        stacked: {
            flexDirection: 'row',
            '& $productName': {
                fontSize: theme.typography.fontSizeXXLarge,
                [theme.breakpoints.down('xs')]: {
                    fontSize: theme.typography.fontSizeXLarge,
                },
            },
        },
        stackedNames: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            marginLeft: theme.spacing(1),
            minWidth: 0,
        },
        markSize: {
            width: '3rem',
            flexShrink: 0,
            lineHeight: 0,
        },
        wordmarkSize: {
            width: '7.5rem',
            maxWidth: '100%',
            lineHeight: 0,
        },

        large: {
            '& $lockupSize': {
                width: '10rem',
                [theme.breakpoints.down('xs')]: {
                    width: '7rem',
                },
            },
            '& $productName': {
                fontSize: theme.typography.fontSizeXLarge,
                [theme.breakpoints.down('xs')]: {
                    fontSize: theme.typography.fontSizeMedium,
                },
            },
        },

        lockup: {
            width: '100%',
        },
        lockupSize: {
            width: '8.5rem',
            minWidth: 0,
            [theme.breakpoints.down('xs')]: {
                width: '6.5rem',
            },
        },
        productName: {
            fontWeight: 700,
            fontSize: theme.typography.fontSizeLarge,
            lineHeight: 1.1,
            whiteSpace: 'nowrap',
            [theme.breakpoints.down('xs')]: {
                fontSize: theme.typography.fontSizeDefault,
            },
        },
    });

const options = {
    name: 'BlackboardIlluminateLogo-',
};

export const useStyles = makeStyles(styles, options);

export const BlackboardIlluminateLogo: React.FunctionComponent<
    IBlackboardIlluminateLogoProps
> = ({
    className,
    theme = 'light',
    orientation = 'inline',
    size = 'default',
}) => {
    const classes = useStyles();
    const color = theme === 'dark' ? BLACKBOARD_OFF_WHITE : BLACKBOARD_GREEN;

    const productName = (
        <span className={classes.productName} style={{ color }} lang="en">
            Illuminate
        </span>
    );
    if (orientation === 'stacked') {
        return (
            <span
                className={classNames(
                    classes.container,
                    classes.stacked,
                    className,
                )}
                data-testid="blackboard-illuminate-logo"
            >
                <span className={classes.markSize}>
                    <BlackboardLogo
                        theme={theme}
                        variant="mark"
                        className={classes.lockup}
                    />
                </span>
                <span className={classes.stackedNames}>
                    <span className={classes.wordmarkSize}>
                        <BlackboardLogo
                            theme={theme}
                            variant="wordmark"
                            className={classes.lockup}
                        />
                    </span>
                    {productName}
                </span>
            </span>
        );
    }

    return (
        <span
            className={classNames(
                classes.container,
                classes.inline,
                size === 'large' && classes.large,
                className,
            )}
            data-testid="blackboard-illuminate-logo"
        >
            <span className={classes.lockupSize}>
                <BlackboardLogo theme={theme} className={classes.lockup} />
            </span>
            {productName}
        </span>
    );
};
